import { useState, useEffect, useMemo, useRef } from 'react';
import {
  Home,
  Bot,
  Package,
  Gem,
  Puzzle,
  FlaskConical,
  Mountain,
  Cog,
  Flame,
  Coins,
  Wrench,
  ShoppingCart,
  TrendingDown,
  BarChart3,
  FileText,
  Repeat,
  Ruler,
  Users,
  Gift,
  Calendar,
  BookOpen,
  FileEdit,
  Tags,
  MessageCircle,
  Pin,
  MapPin,
  ChevronDown,
  Menu,
  HelpCircle,
  Hash,
  User,
  LineChart,
} from 'lucide-react';
import MetalPieces from './MetalPieces';
import DevelopmentPieces from './DevelopmentPieces';
import StockMetalPieces from './StockMetalPieces';
import StockStones from './StockStones';
import Stones from './Stones';
import MinStockMetal from './MinStockMetal';
import MinStockStones from './MinStockStones';
import WorkOrders from './WorkOrders';
import PurchaseOrdersFundicion from './PurchaseOrdersFundicion';
import PurchaseOrdersPiedras from './PurchaseOrdersPiedras';
import CoatingOrders from './CoatingOrders';
import SalesStats from './SalesStats';
import BOM from './BOM';
import Settings from './Settings';
import StoneConsumption from './StoneConsumption';
import Dashboard from './Dashboard';
import UserManagement from './UserManagement';
import Boxes from './Boxes';
import ScheduledReports from './ScheduledReports';
import BusinessRules from './BusinessRules';
import AdminLog from './AdminLog';
import NotificationCenter from './NotificationCenter';
import KeyboardShortcutsHelp from './KeyboardShortcutsHelp';
import { useAppKeyboardShortcuts } from '../hooks/useKeyboardShortcuts';
import { useAuth } from '../contexts/AuthContext';
import { useApi } from '../contexts/ApiContext';
import { useToast } from '../contexts/ToastContext';
import { useUserChat } from '../contexts/UserChatContext';
import SeekersLogoExpanded from '../assets/SeekersLogoExpanded';

import StockMovements from './StockMovements';
import OrderPreparation from './OrderPreparation';
import Montaje from './Montaje';
import ThreePL from './ThreePL';
import Soporte from './Soporte';
import SmartStockDashboard from './SmartStock/SmartStockDashboard';

import AiAssistant from './AiAssistant/AiAssistant';

type TabId =
  | 'dashboard'
  | 'assistant'
  | 'stock-metal-pieces'
  | 'stock-stones'
  | 'movements'
  | 'work-orders'
  | 'fundicion'
  | 'piedras'
  | 'piedras-pedidos'
  | 'revestimiento'
  | 'ventas-stats'
  | 'order-preparation'
  | 'montaje'
  | '3pl'
  | 'soporte'
  | 'metal-pieces'
  | 'development-pieces'
  | 'min-stock-metal'
  | 'min-stock-stones'
  | 'bom'
  | 'stone-consumption'
  | 'settings'
  | 'users'
  | 'boxes'
  | 'scheduled-reports'
  | 'business-rules'
  | 'admin-log'
  | 'smart_stock';

// Mapeo de tabs a secciones de permisos
const TAB_PERMISSION_MAP: Record<TabId, string> = {
  'dashboard': 'dashboard',
  'assistant': 'assistant',
  'stock-metal-pieces': 'stock',
  'stock-stones': 'stock',
  'movements': 'movements',
  'metal-pieces': 'metal-pieces',
  'development-pieces': 'development-pieces',
  'piedras': 'stones',
  'min-stock-metal': 'min-stock-metal',
  'min-stock-stones': 'min-stock-stones',
  'work-orders': 'work-orders',
  'fundicion': 'fundicion',
  'revestimiento': 'revestimiento',
  'piedras-pedidos': 'piedras-pedidos',
  'stone-consumption': 'stone-consumption',
  'ventas-stats': 'ventas-stats',
  'order-preparation': 'order-preparation',
  'montaje': 'montaje',
  '3pl': '3pl',
  'soporte': 'soporte',
  'bom': 'bom',
  'settings': 'settings',
  'users': 'users',
  'boxes': 'boxes',
  'smart_stock': 'smart_stock',
  'scheduled-reports': 'scheduled-reports',
  'business-rules': 'business-rules',
  'admin-log': 'admin-log',
};

interface TabCategory {
  id: string;
  label: string;
  icon?: React.ComponentType<any>;
  tabs: Tab[];
}

const tabCategories: TabCategory[] = [
  {
    id: 'principal',
    label: 'Principal',
    icon: Home,
    tabs: [
      { id: 'dashboard', label: 'Dashboard' },
      { id: 'assistant', label: 'Asistente IA' },
    ],
  },
  {
    id: 'inventario',
    label: 'Inventario',
    icon: Package,
    tabs: [
      { id: 'stock-metal-pieces', label: 'Stock Piezas' },
      { id: 'stock-stones', label: 'Stock Piedras' },
      { id: 'metal-pieces', label: 'Gestión Piezas' },
      { id: 'piedras', label: 'Gestión Piedras' },
      { id: 'development-pieces', label: 'Piezas en Desarrollo', requiresAdmin: true },
    ],
  },
  {
    id: 'produccion',
    label: 'Producción',
    icon: Cog,
    tabs: [
      { id: 'fundicion', label: 'Fundición' },
      { id: 'work-orders', label: 'Órdenes de Fabricación' },
      { id: 'revestimiento', label: 'Revestimiento' },
      { id: 'montaje', label: 'Montaje' },
      { id: '3pl', label: '3PL' },
      { id: 'soporte', label: 'Soporte' },
    ],
  },
  {
    id: 'compras',
    label: 'Compras',
    icon: ShoppingCart,
    tabs: [
      { id: 'smart_stock', label: 'Control Inteligente de Stock' },
      { id: 'piedras-pedidos', label: 'Pedidos Piedras' },
      { id: 'stone-consumption', label: 'Consumo Piedras' },
      { id: 'boxes', label: 'Estuches & Packaging' },
    ],
  },
  {
    id: 'ventas',
    label: 'Ventas',
    icon: BarChart3,
    tabs: [
      { id: 'ventas-stats', label: 'Estadísticas Ventas' },
      { id: 'order-preparation', label: 'Control Pedidos' },
    ],
  },
  {
    id: 'configuracion',
    label: 'Configuración',
    icon: Cog,
    tabs: [
      { id: 'bom', label: 'BOM desde SKU' },
      { id: 'movements', label: 'Movimientos' },
      { id: 'min-stock-metal', label: 'Establecer mínimos - Piezas' },
      { id: 'min-stock-stones', label: 'Establecer mínimos - Piedras' },
      { id: 'settings', label: 'Configuración' },
      { id: 'scheduled-reports', label: 'Reportes Programados' },
      { id: 'business-rules', label: 'Reglas de Negocio' },
      { id: 'users', label: 'Usuarios y Permisos', requiresAdmin: true },
      { id: 'admin-log', label: 'Log', requiresAdmin: true },
    ],
  },
];

const defaultTab: TabId = 'dashboard';
const MOBILE_BREAKPOINT = 768;
const SIDEBAR_WIDTH = 280;

const TAB_ICONS: Partial<Record<TabId, React.ComponentType<any>>> = {
  dashboard: Home,
  assistant: Bot,
  'stock-metal-pieces': Package,
  'stock-stones': Gem,
  'metal-pieces': Puzzle,
  'development-pieces': FlaskConical,
  'piedras': Mountain,
  'work-orders': Cog,
  fundicion: Flame,
  revestimiento: Coins,
  montaje: Wrench,
  '3pl': Package,
  soporte: HelpCircle,
  'piedras-pedidos': ShoppingCart,
  'stone-consumption': TrendingDown,
  'ventas-stats': BarChart3,
  'order-preparation': Package,
  bom: FileText,
  movements: Repeat,
  'min-stock-metal': Ruler,
  'min-stock-stones': Ruler,
  settings: Cog,
  users: Users,
  boxes: Gift,
  'scheduled-reports': Calendar,
  'smart_stock': TrendingDown,
  'business-rules': BookOpen,
  'admin-log': FileEdit,
};

interface Tab {
  id: TabId;
  label: string;
  category?: string;
  requiresAdmin?: boolean;
}

export default function Tabs() {
  const { isAdmin, hasPermission, user, logout } = useAuth();
  const { api } = useApi();
  const { showSuccess, showError } = useToast();
  const { toggleChat, unreadCount, unreadMentions } = useUserChat();
  const sidebarRef = useRef<HTMLElement>(null);
  const [activeTab, setActiveTab] = useState<TabId>(defaultTab);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [expandedCategories, setExpandedCategories] = useState<Set<string>>(new Set());
  const [isMobile, setIsMobile] = useState(() => {
    // Inicializar correctamente desde el inicio, incluso en SSR
    if (typeof window !== 'undefined') {
      return window.innerWidth < MOBILE_BREAKPOINT;
    }
    return false;
  });
  const [metalPieces, setMetalPieces] = useState<any[]>([]);
  const [stones, setStones] = useState<any[]>([]);
  const [multiplier, setMultiplier] = useState<number>(1);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [showMultiplierTooltip, setShowMultiplierTooltip] = useState(false);
  const [showMultiplierEdit, setShowMultiplierEdit] = useState(false);
  const [multiplierInput, setMultiplierInput] = useState<string>('');
  const [showShortcutsHelp, setShowShortcutsHelp] = useState(false);
  const [isBottomNavVisible, setIsBottomNavVisible] = useState(true);
  const lastScrollY = useRef(0);

  // Lógica para ocultar/mostrar menú inferior al hacer scroll
  useEffect(() => {
    if (!isMobile) return;

    const handleScroll = () => {
      const currentScrollY = window.scrollY;

      // Si estamos en la parte superior, siempre mostrar
      if (currentScrollY < 10) {
        setIsBottomNavVisible(true);
      } else {
        // Ocultar al bajar, mostrar al subir
        setIsBottomNavVisible(currentScrollY < lastScrollY.current);
      }

      lastScrollY.current = currentScrollY;
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [isMobile]);

  // Cargar multiplicador
  useEffect(() => {
    const loadMultiplier = async () => {
      try {
        const response = await api.get('/settings/min-stock-multiplier');
        setMultiplier(response.data.multiplier);
      } catch (err) {
        // Error silencioso - no crítico
      }
    };
    loadMultiplier();
  }, [api]);

  useEffect(() => {
    const handleMultiplierUpdated = (event: Event) => {
      const customEvent = event as CustomEvent<{ multiplier: number }>;
      if (typeof customEvent.detail?.multiplier === 'number') {
        setMultiplier(customEvent.detail.multiplier);
      }
    };

    window.addEventListener('multiplier-updated', handleMultiplierUpdated);
    return () => {
      window.removeEventListener('multiplier-updated', handleMultiplierUpdated);
    };
  }, []);

  // Cerrar menú de usuario y edición de multiplicador al hacer clic fuera
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (!target.closest('.sidebar-user-menu') && !target.closest('.content-user-menu')) {
        setShowUserMenu(false);
      }
      if (!target.closest('.content-multiplier-edit')) {
        setShowMultiplierEdit(false);
      }
    };

    if (showUserMenu || showMultiplierEdit) {
      document.addEventListener('click', handleClickOutside);
      return () => {
        document.removeEventListener('click', handleClickOutside);
      };
    }
  }, [showUserMenu, showMultiplierEdit]);

  // Función para actualizar multiplicador
  const handleUpdateMultiplier = async () => {
    // Verificar permiso para editar configuración
    if (!isAdmin && !hasPermission('settings', 'edit')) {
      showError('No tienes permiso para modificar la configuración');
      return;
    }

    try {
      const multiplierValue = parseFloat(multiplierInput);
      if (isNaN(multiplierValue) || multiplierValue < 0.1 || multiplierValue > 10) {
        showError('El multiplicador debe estar entre 0.1 y 10');
        return;
      }

      await api.put('/settings/min-stock-multiplier', {
        multiplier: multiplierValue,
      });

      showSuccess('Multiplicador actualizado correctamente');
      setMultiplier(multiplierValue);
      window.dispatchEvent(
        new CustomEvent('multiplier-updated', {
          detail: { multiplier: multiplierValue },
        })
      );
      setShowMultiplierEdit(false);
      setMultiplierInput('');
    } catch (err: any) {
      showError(err.response?.data?.error || 'Error al actualizar multiplicador');
    }
  };

  const userInitial = user?.username?.charAt(0).toUpperCase() || 'U';


  // Atajos de teclado globales
  useAppKeyboardShortcuts({
    onRefresh: () => {
      // Disparar evento de actualización que los componentes pueden escuchar
      window.dispatchEvent(new CustomEvent('refresh-data'));
    },
    onHelp: () => setShowShortcutsHelp(true),
    enabled: true,
  });

  // Filtrar pestañas según permisos del usuario
  const filteredTabCategories = useMemo(() => {
    return tabCategories.map(category => ({
      ...category,
      tabs: category.tabs.filter(tab => {
        // Si requiere admin y no lo es, fuera
        if (tab.requiresAdmin && !isAdmin) return false;

        // Verificar permiso específico de la sección
        const permissionSection = TAB_PERMISSION_MAP[tab.id];
        if (permissionSection && !isAdmin) {
          return hasPermission(permissionSection, 'view');
        }

        return true;
      })
    })).filter(category => category.tabs.length > 0);
  }, [isAdmin, hasPermission]);

  // Cargar piezas y piedras para la búsqueda
  useEffect(() => {
    const loadData = async () => {
      try {
        const [metalRes, stoneRes] = await Promise.all([
          api.get('/metal-pieces'),
          api.get('/stones')
        ]);
        setMetalPieces(metalRes.data);
        setStones(stoneRes.data);
      } catch (err) {
        // Error silencioso - no crítico para la búsqueda
      }
    };

    if (isAuthenticated()) { // Solo cargar si está autenticado (aunque Tabs se renderiza solo si lo está)
      loadData();
    }
  }, [api]); // isAuthenticated está en AppContent, aquí asumimos autenticado

  // Helper para verificar autenticación simple
  const isAuthenticated = () => true;

  // Detectar si estamos en mobile
  useEffect(() => {
    const checkMobile = () => {
      const isMobileWidth = window.innerWidth < MOBILE_BREAKPOINT;
      setIsMobile(isMobileWidth);
      // Cerrar menú si cambiamos de móvil a desktop
      if (!isMobileWidth && isMobileMenuOpen) {
        setIsMobileMenuOpen(false);
      }
    };

    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, [isMobileMenuOpen]);

  // Cerrar menú móvil cuando cambia el tab
  useEffect(() => {
    if (isMobileMenuOpen && isMobile) {
      setIsMobileMenuOpen(false);
    }
  }, [activeTab, isMobile, isMobileMenuOpen]);

  // Escuchar eventos de navegación desde otros componentes
  useEffect(() => {
    const allTabIds = new Set(tabCategories.flatMap(c => c.tabs.map(t => t.id)));

    const handleNavigateToTab = (event: Event) => {
      const customEvent = event as CustomEvent<{ tabId: string }>;
      if (customEvent.detail?.tabId) {
        const tabId = customEvent.detail.tabId as TabId;
        if (tabId && allTabIds.has(tabId)) {
          setActiveTab(tabId);
          window.scrollTo({ top: 0, behavior: 'smooth' });
        }
      }
    };

    // Escuchar hashchange para compatibilidad
    const handleHashChange = () => {
      const hash = window.location.hash.substring(1);
      if (hash) {
        const tabId = hash as TabId;
        if (tabId && allTabIds.has(tabId)) {
          setActiveTab(tabId);
        }
      }
    };

    window.addEventListener('navigate-to-tab', handleNavigateToTab);
    window.addEventListener('hashchange', handleHashChange);

    // Verificar hash inicial
    handleHashChange();

    return () => {
      window.removeEventListener('navigate-to-tab', handleNavigateToTab);
      window.removeEventListener('hashchange', handleHashChange);
    };
  }, []);

  // Sincronizar activeTab con la URL (hash) para persistencia en refresh
  useEffect(() => {
    if (activeTab) {
      const currentHash = window.location.hash.substring(1);
      if (currentHash !== activeTab) {
        window.location.hash = activeTab;
      }
    }
  }, [activeTab]);

  // Agrupar categorías para el menú móvil usando los mismos iconos que desktop
  const mobileNavItems: Array<{ id: TabId | 'menu'; label: string; icon?: React.ComponentType<any> }> = [
    { id: 'stock-metal-pieces', label: 'Stock Piezas' },
    { id: 'stock-stones', label: 'Stock Piedras' },
    { id: 'work-orders', label: 'Producción' },
    { id: 'ventas-stats', label: 'Ventas' },
    { id: 'menu', label: 'Menú', icon: Menu },
  ];

  const [showMobileMoreMenu, setShowMobileMoreMenu] = useState(false);

  const handleMobileNavClick = (itemId: string) => {
    if (itemId === 'menu') {
      setShowMobileMoreMenu(!showMobileMoreMenu);
    } else {
      setActiveTab(itemId as TabId);
      setShowMobileMoreMenu(false);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const handleTabClick = (tabId: TabId) => {
    setActiveTab(tabId);
    if (isMobile) {
      setIsMobileMenuOpen(false);
    }
  };


  const toggleCategory = (categoryId: string) => {
    setExpandedCategories((prev) => {
      const next = new Set(prev);
      if (next.has(categoryId)) {
        next.delete(categoryId);
      } else {
        next.add(categoryId);
      }
      return next;
    });
  };

  const renderElegantTabs = () => {
    // Obtener la categoría activa
    const activeCategory = filteredTabCategories.find(cat =>
      cat.tabs.some(tab => tab.id === activeTab)
    );

    if (!activeCategory || activeCategory.tabs.length <= 1) {
      return null;
    }

    return (
      <nav className="elegant-tabs content-heading-tabs">
        {activeCategory.tabs.map((tab) => {
          const Icon = TAB_ICONS[tab.id];
          const isActive = activeTab === tab.id;

          return (
            <button
              key={tab.id}
              className={`elegant-tab ${isActive ? 'active' : ''}`}
              onClick={() => setActiveTab(tab.id)}
            >
              {Icon && <Icon size={16} />}
              <span>{tab.label}</span>
            </button>
          );
        })}
      </nav>
    );
  };

  const renderTabContent = () => {
    switch (activeTab) {
      case 'dashboard':
        return <Dashboard />;
      case 'assistant':
        return <AiAssistant />;
      case 'stock-metal-pieces':
        return <StockMetalPieces />;
      case 'stock-stones':
        return <StockStones />;
      case 'movements':
        return <StockMovements />;
      case 'metal-pieces':
        return <MetalPieces />;
      case 'development-pieces':
        return <DevelopmentPieces />;
      case 'piedras':
        return <Stones />;
      case 'min-stock-metal':
        return <MinStockMetal />;
      case 'min-stock-stones':
        return <MinStockStones />;
      case 'work-orders':
        return <WorkOrders />;
      case 'fundicion':
        return <PurchaseOrdersFundicion />;
      case 'revestimiento':
        return <CoatingOrders />;
      case 'piedras-pedidos':
        return <PurchaseOrdersPiedras />;
      case 'stone-consumption':
        return <StoneConsumption />;
      case 'ventas-stats':
        return <SalesStats />;
      case 'order-preparation':
        return <OrderPreparation />;
      case 'montaje':
        return <Montaje />;
      case '3pl':
        return <ThreePL />;
      case 'soporte':
        return <Soporte />;
      case 'bom':
        return <BOM />;
      case 'settings':
        return <Settings />;
      case 'users':
        return <UserManagement />;
      case 'boxes':
        return <Boxes />;
      case 'smart_stock':
        return <SmartStockDashboard />;
      case 'scheduled-reports':
        return <ScheduledReports />;
      case 'business-rules':
        return <BusinessRules />;
      case 'admin-log':
        return <AdminLog />;
      default:
        return null;
    }
  };

  const layoutColumns = isMobile ? '1fr' : `${SIDEBAR_WIDTH}px 1fr`;
  const layoutClasses = ['dashboard-layout'];
  if (isMobile) {
    layoutClasses.push('is-mobile');
  } else {
    layoutClasses.push('layout-sidebar-fixed');
    layoutClasses.push('layout-sidebar-expanded');
  }

  return (
    <div className={layoutClasses.join(' ')} style={{ gridTemplateColumns: layoutColumns }}>
      {/* Header Mobile Simplificado - OCULTO para ganar espacio */}
      {false && isMobile && (
        <header className="mobile-header-modern">
          <div className="mobile-brand-modern">
            {/* Logo removido para ganar espacio en móvil */}
          </div>
          <div className="mobile-actions-modern">
            <button
              className="mobile-action-btn chat-btn"
              onClick={toggleChat}
              style={{ position: 'relative' }}
            >
              <MessageCircle size={18} />
              {unreadCount > 0 && (
                <span
                  style={{
                    position: 'absolute',
                    top: '2px',
                    right: '2px',
                    background: unreadMentions > 0 ? '#f59e0b' : '#ef4444',
                    color: 'white',
                    fontSize: '0.6rem',
                    borderRadius: '50%',
                    width: '14px',
                    height: '14px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    border: '1px solid white'
                  }}
                >
                  {unreadCount}
                </span>
              )}
            </button>
            <NotificationCenter />
            <button
              className="mobile-action-btn user-btn"
              onClick={() => setShowUserMenu(!showUserMenu)}
            >
              {userInitial}
            </button>
          </div>
        </header>
      )}

      {/* Menú de Usuario Mobile (Dropdown) */}
      {isMobile && showUserMenu && (
        <div className="mobile-user-menu-overlay" onClick={() => setShowUserMenu(false)}>
          <div className="mobile-user-menu-content">
            <div className="user-info-card">
              <div className="user-avatar-large">{userInitial}</div>
              <div className="user-details">
                <span className="user-name">{user?.username}</span>
                <span className="user-role">{user?.role}</span>
              </div>
            </div>
            <button className="mobile-menu-item logout" onClick={logout}>
              Cerrar Sesión
            </button>
          </div>
        </div>
      )}

      {/* Menú "Más" (Bottom Sheet) */}
      {isMobile && showMobileMoreMenu && (
        <div className="mobile-more-menu-overlay" onClick={() => setShowMobileMoreMenu(false)}>
          <div className="mobile-more-menu-sheet animate-slide-up">
            <div className="sheet-handle"></div>
            <h3 className="sheet-title">Menú Completo</h3>
            <div className="mobile-grid-menu">
              {filteredTabCategories.map((category) => (
                <div key={category.id} className="mobile-menu-section">
                  <h4 className="mobile-section-title">{category.label}</h4>
                  <div className="mobile-section-grid">
                    {category.tabs.map((tab) => {
                      const TabIcon = TAB_ICONS[tab.id] || category.icon;
                      return (
                        <button
                          key={tab.id}
                          className={`mobile-grid-item ${activeTab === tab.id ? 'active' : ''}`}
                          onClick={() => handleMobileNavClick(tab.id)}
                        >
                          {TabIcon && (
                            <span className="mobile-grid-icon">
                              <TabIcon size={24} />
                            </span>
                          )}
                          <span className="mobile-grid-label">{tab.label}</span>
                        </button>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Sidebar Desktop (Oculto en móvil) */}
      {!isMobile && (
        <aside
          ref={sidebarRef}
          className="sidebar sidebar-modern is-pinned is-expanded"
          style={{ width: `${SIDEBAR_WIDTH}px` }}
        >
          <nav className="sidebar-nav" aria-label="Navegación principal">
            {filteredTabCategories.map((category) => {
              const hasActiveTab = category.tabs.some((tab) => tab.id === activeTab);
              const isExpanded = expandedCategories.has(category.id) || hasActiveTab || !isMobile;

              return (
                <div
                  key={category.id}
                  className={`sidebar-group ${hasActiveTab ? 'is-active' : ''} ${isExpanded ? 'is-expanded' : ''}`}
                >
                  <ul className="sidebar-items" role="menu">
                    {category.tabs.map((tab) => {
                      const TabIcon = TAB_ICONS[tab.id] || category.icon;
                      return (
                        <li key={tab.id}>
                          <button
                            type="button"
                            className={`sidebar-item ${activeTab === tab.id ? 'active' : ''
                              }`}
                            onClick={() => handleTabClick(tab.id)}
                            role="menuitem"
                          >
                            {TabIcon && (
                              <span className="sidebar-item-icon">
                                <TabIcon size={16} />
                              </span>
                            )}
                            <span className="sidebar-item-label">{tab.label}</span>
                          </button>
                        </li>
                      );
                    })}
                  </ul>
                </div>
              );
            })}

          </nav>
        </aside>
      )}

      <div className="dashboard-content">
        <div className="content-shell">
          <div className="content-panel">
            <div className="content-heading">
              {renderElegantTabs()}
              <div className="content-heading-right">
                {!isMobile && (
                  <div className="content-notifications">
                    <NotificationCenter />
                  </div>
                )}
                {!isMobile && (
                  <div className="content-multiplier-edit" style={{ position: 'relative' }}>
                    <button
                      className="content-multiplier-icon"
                      onMouseEnter={() => !showMultiplierEdit && setShowMultiplierTooltip(true)}
                      onMouseLeave={() => setShowMultiplierTooltip(false)}
                      onClick={() => {
                        if (isAdmin) {
                          setShowMultiplierEdit(!showMultiplierEdit);
                          setMultiplierInput(multiplier.toString());
                        }
                      }}
                      title={isAdmin ? `Multiplicador: ${multiplier}x (Click para editar)` : `Multiplicador de stock mínimo: ${multiplier}x`}
                    >
                      <Hash size={18} />
                      <span className="content-multiplier-value">{multiplier}</span>
                      {showMultiplierTooltip && !showMultiplierEdit && (
                        <div className="content-multiplier-tooltip">
                          <div className="content-multiplier-tooltip-label">Multiplicador</div>
                          <div className="content-multiplier-tooltip-value">{multiplier}x</div>
                          {isAdmin && (
                            <div className="content-multiplier-tooltip-hint" style={{ fontSize: '0.7rem', marginTop: '0.25rem', color: 'var(--text-secondary)' }}>
                              Click para editar
                            </div>
                          )}
                        </div>
                      )}
                    </button>
                    {showMultiplierEdit && isAdmin && (
                      <div className="content-multiplier-edit-form" onClick={(e) => e.stopPropagation()}>
                        <input
                          type="number"
                          min="0.1"
                          max="10"
                          step="0.1"
                          value={multiplierInput}
                          onChange={(e) => setMultiplierInput(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                              handleUpdateMultiplier();
                            } else if (e.key === 'Escape') {
                              setShowMultiplierEdit(false);
                              setMultiplierInput('');
                            }
                          }}
                          autoFocus
                          className="content-multiplier-input"
                        />
                        <div className="content-multiplier-edit-actions">
                          <button
                            className="content-multiplier-save"
                            onClick={handleUpdateMultiplier}
                            title="Guardar"
                          >
                            ✓
                          </button>
                          <button
                            className="content-multiplier-cancel"
                            onClick={() => {
                              setShowMultiplierEdit(false);
                              setMultiplierInput('');
                            }}
                            title="Cancelar"
                          >
                            ×
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                )}
                {!isMobile && (
                  <div className="content-user-menu">
                    <button
                      className="content-user-icon"
                      onClick={() => setShowUserMenu(!showUserMenu)}
                      aria-label="Menú de usuario"
                      aria-expanded={showUserMenu}
                      title={user?.username}
                    >
                      <User size={18} />
                    </button>
                    {showUserMenu && (
                      <div className="content-user-dropdown">
                        <div className="content-user-info-full">
                          <div className="content-user-info-avatar">{userInitial}</div>
                          <div className="content-user-info-details">
                            <div className="content-user-info-name">{user?.username}</div>
                            <div className="content-user-info-role">
                              {user?.role === 'ADMINISTRADOR' ? 'Administrador' : user?.role}
                            </div>
                          </div>
                        </div>
                        <button
                          className="content-user-logout"
                          onClick={async () => {
                            await logout();
                            setShowUserMenu(false);
                          }}
                        >
                          Cerrar sesión
                        </button>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
            <div className="content-body">{renderTabContent()}</div>
          </div>
        </div>
      </div>

      {/* Bottom Navigation Bar (Solo Móvil) */}
      {isMobile && (
        <nav className={`mobile-bottom-nav ${!isBottomNavVisible ? 'mobile-bottom-nav-hidden' : ''}`}>
          {mobileNavItems.map((item) => {
            const IconComponent = item.icon || TAB_ICONS[item.id as TabId];
            return (
              <button
                key={item.id}
                className={`mobile-nav-item ${(activeTab === item.id) || (item.id === 'menu' && showMobileMoreMenu)
                  ? 'active'
                  : ''
                  }`}
                onClick={() => handleMobileNavClick(item.id)}
              >
                {IconComponent && (
                  <span className="nav-icon">
                    <IconComponent size={20} />
                  </span>
                )}
                <span className="nav-label">{item.label}</span>
              </button>
            );
          })}
        </nav>
      )}

      <KeyboardShortcutsHelp
        isOpen={showShortcutsHelp}
        onClose={() => setShowShortcutsHelp(false)}
      />
    </div>
  );
}
